import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Web NFC reader lifecycle for in-app deck scanning.
 *
 * Web NFC (`NDEFReader`) lets an already-open page read an NFC tag without the
 * OS bouncing the user out to a fresh browser tab — a more seamless "tap to
 * host" than the passive URL open. It's Android-Chrome/Edge only and needs a
 * secure context plus a user gesture to start; everywhere else {@link isNfcSupported}
 * is false and the caller keeps the passive-tap copy.
 *
 * `start()` opens a scan session (which triggers the one-time NFC permission
 * prompt on first use) and resolves the first tag read to its first URL record,
 * handed to `onUrl`.
 *
 * ## Why the session outlives the read
 *
 * An open scan is the *only* thing suppressing Android's own tag dispatch:
 * Chrome holds `NfcAdapter.enableReaderMode()` for as long as a watch is
 * registered, and while it does, tags come here instead of to the platform
 * handler. Drop the watch and the platform takes the tag back.
 *
 * A deck is held flat against the phone for a good second or more — through the
 * read, and through whatever the caller does with the URL. Aborting on the read
 * (as this hook used to) released reader mode with the card still in the field,
 * so Android re-discovered it, dispatched the URL record itself, and opened the
 * tag URL in a new client — on top of the in-app read that had already
 * succeeded. Worse, that second physical read advances the tag's SUN counter, so
 * the two handoffs race and the loser is rejected by the backend as stale.
 *
 * So the session is held open and the read is de-duplicated with a latch
 * instead: one tap still yields exactly one handoff, but reader mode stays held
 * across the caller's validation, and past `stop()` by {@link READER_DWELL_MS}
 * whenever a tag was recently in the field. `start()` on a live session just
 * re-opens the latch — a retry never surrenders reader mode either.
 */

export type NfcScanError = "unsupported" | "denied" | "failed"

interface UseNfcScan {
    /** Whether Web NFC is usable on this device/context. */
    supported: boolean
    /** True from when `start()` is called until `stop()`. A read no longer ends
     *  the session — see the note above. */
    scanning: boolean
    /**
     * True only once `scan()` has resolved — i.e. the NFC permission is granted
     * and the browser holds NFC reader mode. This is the moment it's safe to
     * invite a tap: before it, Android's own tag dispatch (opening the URL in a
     * browser) can still pre-empt the in-page read.
     */
    armed: boolean
    start: () => void
    stop: () => void
}

/**
 * How long reader mode is held past `stop()` when a tag was in the field just
 * before it. Long enough to cover a hand still resting on the phone through the
 * caller's validation round trip; short enough that a deliberate tap moments
 * later still reaches the OS. Reads during the dwell are swallowed — the point
 * is purely to deny the platform dispatcher the tag, not to keep listening.
 */
const READER_DWELL_MS = 2500

interface Session {
    controller: AbortController
    /** Latched on a handled read so one tap yields one handoff. Re-opened by
     *  `start()`; permanently closed once the session is retired. */
    handled: boolean
    /** When a tag was last in the field (read or read error), or null if none
     *  has been. Drives the dwell on `stop()`. */
    lastTagAt: number | null
}

/** Feature-detect Web NFC. Shared so the tile affordance and the hook agree. */
export const isNfcSupported = (): boolean =>
    typeof window !== "undefined" && "NDEFReader" in window

/** Pull the first URL record's text out of a scanned NDEF message. */
function decodeUrlRecord(message: NDEFMessage): string | null {
    for (const record of message.records) {
        // Web NFC surfaces URI records as recordType "url"; be liberal about the
        // raw "U" form some tag stacks report.
        if (record.recordType !== "url" && record.recordType !== "U") continue
        if (!record.data) continue
        return new TextDecoder(record.encoding ?? "utf-8").decode(record.data)
    }
    return null
}

export function useNfcScan(
    onUrl: (url: string) => void,
    onError: (error: NfcScanError) => void,
): UseNfcScan {
    const supported = isNfcSupported()
    const [scanning, setScanning] = useState(false)
    const [armed, setArmed] = useState(false)
    const sessionRef = useRef<Session | null>(null)

    // Keep the latest callbacks in refs so a scan started once doesn't need to be
    // torn down and rebuilt every time the consumer re-renders.
    const onUrlRef = useRef(onUrl)
    const onErrorRef = useRef(onError)
    useEffect(() => {
        onUrlRef.current = onUrl
        onErrorRef.current = onError
    })

    const stop = useCallback(() => {
        const session = sessionRef.current
        sessionRef.current = null
        setScanning(false)
        setArmed(false)
        if (!session) return

        // Retire it first: a session that's no longer current must never reach a
        // callback, including during the dwell below.
        session.handled = true

        const remaining = session.lastTagAt === null
            ? 0
            : Math.max(0, READER_DWELL_MS - (Date.now() - session.lastTagAt))

        if (remaining === 0) session.controller.abort()
        else setTimeout(() => session.controller.abort(), remaining)
    }, [])

    const start = useCallback(() => {
        if (!supported) { onErrorRef.current("unsupported"); return }

        const existing = sessionRef.current
        if (existing) {
            // Already holding reader mode. Releasing and re-acquiring it is the
            // exact gap this hook exists to close, so a re-arm is just the latch.
            existing.handled = false
            return
        }

        const session: Session = {
            controller: new AbortController(),
            handled: false,
            lastTagAt: null,
        }
        sessionRef.current = session
        setScanning(true)
        setArmed(false)

        const { signal } = session.controller
        const reader = new NDEFReader()

        reader.addEventListener("reading", (event) => {
            session.lastTagAt = Date.now()
            if (session.handled) return
            const url = decodeUrlRecord(event.message)
            if (!url) return
            // One tap → one handoff. The latch does that job; the session stays
            // open so reader mode isn't handed back mid-tap.
            session.handled = true
            onUrlRef.current(url)
        }, { signal })

        reader.addEventListener("readingerror", () => {
            // A tag was against the phone even though it didn't read — which is
            // exactly when the platform dispatcher would like a turn.
            session.lastTagAt = Date.now()
            onErrorRef.current("failed")
        }, { signal })

        reader.scan({ signal })
            // Resolves only after permission is granted and reader mode is held.
            // Until this fires, a tap can still be grabbed by the OS, so the UI
            // waits on `armed` before telling the user to tap.
            .then(() => { if (!signal.aborted) setArmed(true) })
            .catch((err: unknown) => {
                if (signal.aborted) return // stop() raced us — not an error
                if (sessionRef.current === session) sessionRef.current = null
                session.handled = true
                session.controller.abort()
                setScanning(false)
                setArmed(false)
                const denied = err instanceof DOMException && err.name === "NotAllowedError"
                onErrorRef.current(denied ? "denied" : "failed")
            })
    }, [supported])

    // Abort any in-flight scan when the consumer unmounts.
    useEffect(() => stop, [stop])

    return { supported, scanning, armed, start, stop }
}
